import ol = require("ol");
import $ = require("jquery");
import Query = require("arcgis/services/featureserver/query");
import Converter = require("../../utils/ol3/converter");

interface IFieldInfo {
    name: string;
    type: string;
    alias: string;
    nullable: boolean
};

export class WfsDescribeFeatureReader {

    static about = `DescribeFeatureType proxy`;

    static read(args: {
        url: string,
        typeName: string
    }) {

        var version = "1.1.0";
        var typeName = args.typeName;

        var data = `
<DescribeFeatureType
  version="${version}"
  service="WFS"
  xmlns="http://www.opengis.net/wfs"
  xmlns:usa="http://census.gov"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
    <TypeName>${typeName}</TypeName>
</DescribeFeatureType>`;

        var d = $.Deferred<Array<IFieldInfo>>();

        var req = $.ajax({
            type: "POST",
            proxy: true,
            url: args.url,
            contentType: "text/xml",
            dataType: "text",
            data: data
        });

        req.done((data, status) => {
            var xmlDoc = $.parseXML(data);
            var json = WfsDescribeFeatureConverter.convert(xmlDoc);
            d.resolve(json);
        });

        return d;
    }
}

export class WfsGetFeatureReader {

    static about = `GetFeature proxy`;

    static read(args: {
        url: string;
        typeName: string;
        filter?: Array<Array<string>>;
        fields?: Array<Query.Field>;
        fid?: string;
    }) {
        var url = args.url;
        var typeName = args.typeName;
        var filter = "";
        if (args.fid) filter += `<ogc:FeatureId fid="${args.fid}"/>`;
        if (args.filter) {
            filter += "<And>";
            filter += args.filter
                .filter(nv => !!nv[1])
                .map(nv => `<PropertyIsEqualTo><PropertyName>${nv[0]}</PropertyName><Literal>${nv[1]}</Literal></PropertyIsEqualTo>`).join("\n");
            filter += "</And>";
        }

        var data = `
<wfs:GetFeature
  service="WFS"
  version="1.1.0"
  xmlns:usa="http://census.gov"
  xmlns:wfs="http://www.opengis.net/wfs"
  xmlns:ogc="http://www.opengis.net/ogc"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.opengis.net/wfs
                      http://schemas.opengis.net/wfs/1.1.0/wfs.xsd">
  <wfs:Query typeName="${typeName}">
    <ogc:Filter>
       ${filter}
    </ogc:Filter>
    </wfs:Query>
</wfs:GetFeature>`;

        /*
<ows:ExceptionReport xmlns:xs="http://www.w3.org/2001/XMLSchema" xmlns:ows="http://www.opengis.net/ows" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" version="1.0.0" xsi:schemaLocation="http://www.opengis.net/ows http://localhost:8080/geoserver/schemas/ows/1.0.0/owsExceptionReport.xsd">
  <ows:Exception exceptionCode="InvalidParameterValue">
    <ows:ExceptionText>Illegal property name: geo-field-STATE_ABBR for feature type usa:states</ows:ExceptionText>
  </ows:Exception>
</ows:ExceptionReport>
        */

        var d = $.Deferred<Rhythm.GIS.FeatureQueryResultLayer>();

        var req = $.ajax({
            type: "POST",
            proxy: true,
            url: url,
            contentType: "text/xml",
            dataType: "text",
            data: data
        });

        req.done((data, status) => {
            let xmlDoc = $.parseXML(data);
            let layer = WfsGetFeatureConverter.convert(xmlDoc, args.fields);
            d.resolve(layer);
        });

        return d;
    }

    // ?request=GetFeature&version=1.1.0&typeName=usa:states&propertyName=NAME10,DP0010001&BBOX=-75.102613,40.212597,-72.361859,41.512517,EPSG:4326
    static query(args: {
        url: string;
        typeName: string;
        geometry: Array<number>;
    }) {
        var d = $.Deferred<Rhythm.GIS.FeatureQueryResultLayer>();

        var url = `${args.url}?request=GetFeature&version=1.1&typeName=${args.typeName}&BBOX=${args.geometry.join(",") },EPSG:4326&srsName=urn:x-ogc:def:crs:EPSG:4326`;

        var metadataReq = WfsDescribeFeatureReader.read({
            url: args.url,
            typeName: args.typeName
        });

        var req = $.ajax({
            type: "GET",
            proxy: true,
            url: url,
            contentType: "text/xml",
            dataType: "text"
        });

        metadataReq.done(fields => {

            req.done((data, status) => {
                var xmlDoc = $.parseXML(data);
                let layer = WfsGetFeatureConverter.convert(xmlDoc, fields);
                layer.spatialReference = { wkid: <any>"EPSG:4326" };
                d.resolve(layer);
            });
        });

        return d;
    }
}

export class WfsDescribeFeatureConverter {

    static about = `Converts DescribeFeatureType response to IFieldInfo items`;

    private static typeMapping = {
        "int": "number",
        "double": "number",
        "string": "string"
    };

    private static removeNs(value: string) {
        var ns = value.indexOf(":");
        return (0 < ns) ? value.substring(ns + 1) : value;
    }

    private static mapType(value: string) {
        return <string>(this.typeMapping[value] || null);
    }

    static convert(xml: XMLDocument) {
        var $xml = $(xml);
        var result = $xml.find("sequence").find("element").toArray().map(e => {
            var item = $(e);
            return <IFieldInfo>{
                name: this.removeNs(item.attr("name")),
                alias: this.removeNs(item.attr("name")),
                type: this.mapType(this.removeNs(item.attr("type"))),
                nullable: !!item.attr("nillable") || true
            };
        });

        return result;
    }

}

export class WfsGetCapabilitiesConverter {

    static about = `Extracts operations and feature types from GetCapabilities response.`;

    static getUrl(operation: JQuery) {
        var dcp = operation.find("DCP").find("HTTP");
        var url = dcp.find("Post").attr("xlink:href");
        if (!url) url = dcp.find("Get").attr("xlink:href");
        return url;
    }

    static convert(xml: XMLDocument) {
        var $xml = $(xml);

        var result = {
            operations: {},
            featureTypes: {}
        };

        $xml.find("OperationsMetadata").find("Operation").each((i, e) => {
            var item = $(e);
            result.operations[item.attr("name")] = WfsGetCapabilitiesConverter.getUrl(item);
        });

        $xml.find("FeatureTypeList").find("FeatureType").each((i, e) => {
            var item = $(e);
            var bbox = item.find("WGS84BoundingBox");

            result.featureTypes[item.find("Name").text()] = {
                srs: item.find("DefaultSRS").text(),
                extent: (bbox.find("LowerCorner").text() + " " + bbox.find("UpperCorner").text()).split(" ").join(","),
                operators: {}
            };
        });

        return result;
    }
}

export class WfsGetFeatureConverter {

    static about = `Converts GetFeature response to a layer of features.`;

    static convert(xml: XMLDocument, metadata?: Array<Query.Field>) {
        let wfs = new ol.format.WFS();
        let proj = wfs.readProjectionFromDocument(xml);
        if (!proj) {
            console.log("unable to determine projection, assuming empty response");
            let result = <Rhythm.GIS.FeatureQueryResultLayer>{};
            result.features = [];
            return result;
        }
        // instead of worrying about the axis order require service to support this very common projection with predictable results
        //console.assert(proj.getAxisOrientation() !== "neu");
        console.assert(proj.getCode() === "urn:x-ogc:def:crs:EPSG:4326");

        let features = wfs.readFeaturesFromDocument(xml);

        // hack to convert strings back to numbers
        // alternative: String.prototype.toFixed = function (x) { return parseFloat(this).toFixed(x); }
        if (metadata) {
            metadata.forEach(field => {
                if (field.type === "number") {
                    features.forEach(f => {
                        let v = f.get(field.name);
                        if (typeof v === "string") {
                            f.set(field.name, parseFloat(v));
                        }
                    });
                }
            });
        }

        // too many headaches around axis-order, always use EPSG:4326
        let converter = new Converter("EPSG:4326");
        // strange but true...the reader is returning ol3 features but to be compatible
        // with the ags reader we need to 'normalize' the features
        // only to be turned back into ol3 features (may optimize later).
        return converter.fromNativeFeatures(features);
    }
}

 